import React, { useState, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Users, Wifi, ShieldCheck, DollarSign, Clock, CheckCircle2, XCircle, X } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [vers, setVers] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("verifications");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const [s, v, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/verifications?status=pending"), api.get("/admin/users")]);
      setStats(s.data); setVers(v.data); setUsers(u.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const decide = async (id, action) => {
    try { await api.post(`/admin/verifications/${id}/${action}`, { note: action === "reject" ? "Please re-upload clearer documents." : "Approved" }); toast.success(`Verification ${action}d`); setPreview(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const fileUrl = (path) => `${API}/files/${path}?auth=${encodeURIComponent(localStorage.getItem("opn_token") || "")}`;

  const metrics = [
    { icon: Users, label: "Total users", value: stats?.total_users },
    { icon: Wifi, label: "Hotspots", value: stats?.hotspots },
    { icon: ShieldCheck, label: "Pending reviews", value: stats?.pending_verifications },
    { icon: DollarSign, label: "Revenue", value: stats ? `$${stats.revenue}` : "—" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-1">Admin Panel</h1>
        <p className="text-muted-foreground mb-8">Platform oversight & trust management.</p>

        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-10">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-metric-${m.label.toLowerCase().replace(/\s/g,'-')}`}>
              <m.icon size={18} className="text-muted-foreground mb-3" />
              <div className="font-display text-2xl font-bold tracking-tight">{m.value ?? "—"}</div>
              <div className="text-xs text-muted-foreground mt-1">{m.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6 border-b border-border">
          {["verifications", "users"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? "border-foreground text-foreground" : "border-transparent text-muted-foreground"}`}>
              {t === "verifications" ? `Verification Queue (${vers.length})` : `Users (${users.length})`}
            </button>
          ))}
        </div>

        {tab === "verifications" ? (
          vers.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center"><CheckCircle2 size={28} className="mx-auto mb-3 text-emerald-500" /><p className="text-muted-foreground">No pending verifications. All clear!</p></div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="verification-queue">
              {vers.map((v) => (
                <div key={v.verification_id} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center gap-2 mb-3"><Clock size={14} className="text-amber-500" /><span className="font-mono text-xs uppercase tracking-wider text-amber-500">Pending</span></div>
                  <p className="font-medium">{v.user_name || "User"}</p>
                  <p className="text-xs text-muted-foreground mb-4">{v.user_email}</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <img src={fileUrl(v.id_document_path)} alt="ID" className="rounded-lg border border-border aspect-video object-cover bg-accent cursor-pointer" onClick={() => setPreview(fileUrl(v.id_document_path))} data-testid={`ver-id-img-${v.verification_id}`} onError={(e) => { e.target.style.opacity = 0.3; }} />
                    <img src={fileUrl(v.selfie_path)} alt="Selfie" className="rounded-lg border border-border aspect-video object-cover bg-accent cursor-pointer" onClick={() => setPreview(fileUrl(v.selfie_path))} onError={(e) => { e.target.style.opacity = 0.3; }} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => decide(v.verification_id, "approve")} data-testid={`approve-${v.verification_id}`} className="flex-1 h-9 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-1"><CheckCircle2 size={14} /> Approve</button>
                    <button onClick={() => decide(v.verification_id, "reject")} data-testid={`reject-${v.verification_id}`} className="flex-1 h-9 rounded-lg border border-border text-sm hover:bg-accent transition-colors flex items-center justify-center gap-1"><XCircle size={14} /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="rounded-2xl border border-border overflow-hidden" data-testid="users-table">
            <table className="w-full text-sm">
              <thead className="bg-card/60 text-muted-foreground"><tr><th className="text-left font-medium px-4 py-3">Name</th><th className="text-left font-medium px-4 py-3">Email</th><th className="text-left font-medium px-4 py-3">Role</th><th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Verified</th><th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Membership</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3"><span className="font-mono text-xs px-2 py-0.5 rounded-full bg-accent capitalize">{u.role}</span></td>
                    <td className="px-4 py-3 hidden sm:table-cell">{u.verification_status === "verified" ? <span className="text-emerald-500 flex items-center gap-1"><CheckCircle2 size={14} /> yes</span> : <span className="text-muted-foreground capitalize">{u.verification_status}</span>}</td>
                    <td className="px-4 py-3 hidden sm:table-cell capitalize text-muted-foreground">{u.subscription_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {preview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <button className="absolute right-6 top-6 text-white"><X size={24} /></button>
          <img src={preview} alt="document" className="max-h-[85vh] max-w-full rounded-xl border border-border" />
        </div>
      )}
    </div>
  );
}
